import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { evaluate } from 'mathjs'
import { v4 as uuid } from 'uuid'

import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generatePemdasEquation } from '~/bootcamp/Activities/PEMDAS/utils/pemdas.utils'

import Equation from '~/bootcamp/Activities/PEMDAS/components/Equation'

function PEMDAS({
  difficultyLevel,
  actionButtonsFC: AnswerButton,
  autofocus = true,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const [equation, setEquation] = useState<string>('')
  const [answer, setAnswer] = useState<string>('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [pattern, setPattern] = useState<string>('')
  const { t } = useTranslation('generics')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const generateEquation = useCallback(() => {
    const { equation: newEquation, pattern: newPattern } = generatePemdasEquation(difficultyLevel, taskHistory)

    setEquation(newEquation)
    setPattern(newPattern)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    generateEquation()
  }, [generateEquation])

  useEmitInstance(onInstanceChange, equation || null)

  const clearAnswer = (): void => {
    setAnswer('')
    onAnswerStateChange(null)
  }

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = parseInt(answer, 10) === evaluate(equation)
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    clearAnswer()
    setTaskHistory((currHistory) => {
      return [pattern, ...currHistory].slice(0, 3)
    })
    generateEquation()
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="justify-between">
      <span />
      <div className="flex flex-wrap items-center space-x-1 justify-center">
        <Equation statement={equation} />
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertResult')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={4}
            onChange={setAnswer}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </div>

      {AnswerButton && (
        <AnswerButton
          clearFormAction={clearAnswer}
          correctAnswer={answerState ?? false}
          formValid={answer !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default PEMDAS
